import { Check, Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import type { UseVoiceComposer } from "../hooks/use-voice-composer";
import { formatRecordingTime, WAVEFORM_BAR_COUNT } from "../hooks/use-voice-recorder";

// Stable per-position keys for the fixed-length waveform buffer (each slot's identity is its
// position, since the buffer scrolls by value — index is the identity, but keying by a constant
// id keeps the list reconciliation honest without an index-as-key lint suppression).
const WAVEFORM_KEYS = Array.from({ length: WAVEFORM_BAR_COUNT }, (_, i) => `wave-${i}`);
// Bar (2px) + gap (1px). The visible bar count is derived from the container width so the
// waveform spans the full composer regardless of surface width, not a hardcoded count.
const WAVEFORM_BAR_PITCH = 3;

/**
 * Scrolling waveform. The buffer is oldest → newest; we render the last `barCount` samples
 * (barCount derived from the measured container width) so it always fills the row. The newest
 * amplitude hugs the right; quiet samples collapse to short baseline dots — which is also how a
 * silence-pause reads, since the recorder freezes the buffer while paused.
 */
function RecordingWaveform({ levels }: { levels: number[] }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [barCount, setBarCount] = useState(0);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      setBarCount(Math.max(0, Math.floor(node.clientWidth / WAVEFORM_BAR_PITCH)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const visible = barCount > 0 ? levels.slice(-barCount) : [];
  const offset = levels.length - visible.length;

  return (
    <div
      ref={containerRef}
      className="flex h-7 min-w-0 flex-1 items-center justify-end gap-px overflow-hidden"
      aria-hidden="true"
    >
      {visible.map((level, index) => {
        const height = Math.max(2, Math.round(level * 24));
        const active = level > 0.06;
        return (
          <span
            key={WAVEFORM_KEYS[offset + index]}
            className={cn("w-0.5 shrink-0", active ? "bg-foreground" : "bg-neutral-dim/60")}
            style={{ height: `${height}px` }}
          />
        );
      })}
    </div>
  );
}

/**
 * Redesign v2 recording strip that takes over the composer input row while recording.
 * Border never changes (the waveform alone signals the active state); "stop" is split into
 * an explicit discard (✕) / accept-and-transcribe (✓) pair.
 */
export function ComposerRecordingStrip({
  voice,
  className,
  showAddContextPlaceholder = true,
}: {
  voice: UseVoiceComposer;
  className?: string;
  /** Render the disabled "+" placeholder that visually aligns the composer recording strip. */
  showAddContextPlaceholder?: boolean;
}) {
  return (
    <output
      className={cn("flex w-full items-center gap-2.5", className)}
      aria-label={voice.isPaused ? "Recording paused — speak to continue" : "Recording"}
    >
      {showAddContextPlaceholder ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="size-7 shrink-0 p-0 text-muted-foreground/50"
          disabled
          aria-label="Add context"
          tabIndex={-1}
        >
          <Plus className="size-[18px]" />
        </Button>
      ) : null}
      <RecordingWaveform levels={voice.levels} />
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
        {formatRecordingTime(voice.elapsedMs)}
      </span>
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        className="size-7 shrink-0 p-0"
        onClick={voice.discard}
        aria-label="Discard recording"
      >
        <X className="size-3.5" />
      </Button>
      <Button
        type="button"
        variant="default"
        size="icon-sm"
        className="size-7 shrink-0 p-0"
        onClick={() => void voice.accept()}
        disabled={voice.isStopping}
        aria-label="Transcribe recording into the message"
      >
        {voice.isStopping ? <Spinner className="size-3.5" /> : <Check className="size-3.5" />}
      </Button>
    </output>
  );
}
